import { h, Fragment } from 'preact';
import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Fab from '@mui/material/Fab';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CloseIcon from '@mui/icons-material/Close';
import ThumbDownOutlinedIcon from '@mui/icons-material/ThumbDownOutlined';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';

import AskUser from './AskUser.jsx';
import { createBridge } from './bridge.js';
import { resetConversation, startCopilotRun } from './chat.js';
import { mergeAssistantReply } from './replies.js';
import { useOptionalWorld, useWorldObjects } from './hooks.jsx';
import { renderMarkdown } from './markdown.js';
import { ReplyFeedbackForm, useReplyFeedback } from './replyFeedback.jsx';
import ToolActivity from './ToolActivity.jsx';

/*
 * Visual dialect mirrors reactdashboard's CopilotRail (floating window,
 * bottom-left launcher, brand header, suggestion chips, primary/user vs
 * hover/assistant bubbles, rounded composer with a round arrow send button)
 * so Copilot looks the same on this dashboard as on the CMS dashboards.
 */

const SUGGESTIONS = [
  'What can I do on this dashboard?',
  'List my endpoints',
  "What's in the LRU cache?",
  'Show the change history for this endpoint',
  'Show recent execution logs',
];

const WINDOW_SX = {
  position: 'fixed',
  bottom: 24,
  left: 24,
  width: 380,
  maxWidth: 'calc(100vw - 48px)',
  height: 560,
  maxHeight: 'calc(100vh - 48px)',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  borderRadius: '12px',
  border: 1,
  borderColor: 'divider',
  boxShadow: '0 10px 28px rgba(0, 0, 0, 0.2)',
  zIndex: theme => theme.zIndex.drawer + 2,
};

const LAUNCHER_SX = {
  position: 'fixed',
  bottom: 24,
  left: 24,
  zIndex: theme => theme.zIndex.drawer + 2,
};

const BUBBLE_SX = {
  maxWidth: '85%',
  px: '14px',
  py: '10px',
  borderRadius: '14px',
  fontSize: 13,
  lineHeight: 1.45,
};

const RICH_SX = {
  '& p': { m: 0, mb: 1 },
  '& p:last-child': { mb: 0 },
  '& ol, & ul': { m: 0, mb: 1, pl: '20px' },
  '& li': { mb: 0.5 },
  '& h1, & h2, & h3': { fontSize: 13.5, m: 0, mb: 0.75 },
  '& code': { bgcolor: 'action.selected', borderRadius: '4px', px: '5px', py: '1px', fontSize: 12 },
  '& pre': {
    bgcolor: 'action.selected',
    borderRadius: '8px',
    px: '10px',
    py: 1,
    overflowX: 'auto',
    fontSize: 12,
    m: 0,
    mb: 1,
    whiteSpace: 'pre-wrap',
  },
  '& a': { color: 'primary.main' },
};

function Bubble(props) {
  const { role, children, sx } = props;
  const isUser = role === 'user';
  return (
    <Box sx={{ display: 'flex', justifyContent: isUser ? 'flex-end' : 'flex-start', mb: 1.5 }}>
      <Box
        sx={{
          ...BUBBLE_SX,
          bgcolor: isUser ? 'primary.main' : 'action.hover',
          color: isUser ? 'primary.contrastText' : 'text.primary',
          borderBottomRightRadius: isUser ? '4px' : undefined,
          borderBottomLeftRadius: isUser ? undefined : '4px',
          ...(sx || {}),
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

export default function CopilotPanel(_props) {
  const world = useOptionalWorld();
  useWorldObjects();

  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [running, setRunning] = useState(false);
  const [status, setStatus] = useState('');
  const [messages, setMessages] = useState([]);
  const [expandedToolId, setExpandedToolId] = useState(null);
  const [approval, setApproval] = useState(null);
  const approvalResolveRef = useRef(null);
  const bodyRef = useRef(null);
  const replyFeedback = useReplyFeedback(messages);

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [messages, status, open]);

  const pushMessage = useCallback(function pushMessage(message) {
    setMessages(current => [...current, message]);
  }, []);

  const requestApproval = useCallback(function requestApproval(request) {
    return new Promise(resolve => {
      approvalResolveRef.current = resolve;
      setApproval(request);
    });
  }, []);

  const resolveApproval = useCallback(function resolveApproval(result) {
    if (approvalResolveRef.current) {
      approvalResolveRef.current(result);
      approvalResolveRef.current = null;
    }
    setApproval(null);
  }, []);

  const bridge = useMemo(
    function makeBridge() {
      return world ? createBridge(world, requestApproval) : null;
    },
    [world, requestApproval]
  );

  const newChat = useCallback(
    function newChat() {
      if (running) return;
      resetConversation();
      replyFeedback.reset();
      setMessages([]);
      setStatus('');
      setExpandedToolId(null);
    },
    [replyFeedback, running]
  );

  const send = useCallback(
    async function send(text) {
      const prompt = (text || '').trim();
      if (!prompt || running || !bridge) {
        return;
      }
      setDraft('');
      setRunning(true);
      setStatus('Thinking…');
      pushMessage({ role: 'user', text: prompt });
      const finish = () => {
        setRunning(false);
        setStatus('');
      };
      try {
        await startCopilotRun(
          prompt,
          {
            onStatus: message => setStatus(message),
            onAssistant: (reply, meta) =>
              setMessages(current =>
                mergeAssistantReply(current, { role: 'assistant', text: reply, runId: meta && meta.runId })
              ),
            onError: message => {
              pushMessage({ role: 'error', text: message });
              finish();
            },
            onDone: finish,
            onToolCall: tool =>
              pushMessage({
                role: 'tool',
                tool: {
                  id: tool.id,
                  title: tool.description || tool.name,
                  state: 'running',
                  input: tool.input,
                },
              }),
            onToolResult: (tool, result, isError) =>
              setMessages(current =>
                current.map(message =>
                  message.role === 'tool' && message.tool.id === tool.id
                    ? { ...message, tool: { ...message.tool, state: isError ? 'error' : 'done', result } }
                    : message
                )
              ),
            onAskUser: request => pushMessage({ role: 'ask_user', askUser: request }),
          },
          { clientTools: bridge.getTools(), execute: bridge.handleToolCall }
        );
      } catch (error) {
        pushMessage({ role: 'error', text: error instanceof Error ? error.message : String(error) });
        finish();
      }
    },
    [bridge, pushMessage, running]
  );

  const window_ = (
    <Paper sx={WINDOW_SX}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: '14px' }}>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '7px', fontWeight: 700, fontSize: 16 }}>
          <AutoAwesomeIcon fontSize="small" color="primary" />
          Copilot
        </Box>
        <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
          <IconButton size="small" aria-label="New chat" title="New chat" disabled={running} onClick={newChat}>
            <AddIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" aria-label="Close Copilot" title="Close Copilot" onClick={() => setOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>
      <Box ref={bodyRef} sx={{ flex: '1 1 auto', overflowY: 'auto', px: 2, pt: 1, pb: 1.5 }}>
        {messages.length === 0 && (
          <Fragment>
            <Typography sx={{ fontSize: 13.5, fontWeight: 700, mt: '10px', mb: '12px' }}>
              Here are some things I can help with:
            </Typography>
            {SUGGESTIONS.map(suggestion => (
              <Box
                key={suggestion}
                component="button"
                type="button"
                onClick={() => send(suggestion)}
                sx={{
                  display: 'block',
                  width: 'fit-content',
                  maxWidth: '100%',
                  m: '0 0 10px',
                  px: 2,
                  py: '9px',
                  border: 'none',
                  borderRadius: '999px',
                  bgcolor: 'action.hover',
                  color: 'text.primary',
                  fontSize: 13,
                  fontFamily: 'inherit',
                  textAlign: 'left',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.selected' },
                }}
              >
                {suggestion}
              </Box>
            ))}
          </Fragment>
        )}
        {messages.map((message, index) => (
          <Fragment key={index}>
            {message.role === 'assistant' ? (
              <Bubble role="assistant" sx={RICH_SX}>
                <div dangerouslySetInnerHTML={{ __html: renderMarkdown(message.text) }} />
              </Bubble>
            ) : message.role === 'ask_user' ? (
              <Bubble role="assistant" sx={{ maxWidth: '100%', width: '100%' }}>
                <AskUser request={message.askUser} />
              </Bubble>
            ) : message.role === 'tool' ? (
              <ToolActivity
                tool={message.tool}
                expanded={expandedToolId === message.tool.id}
                onToggle={() =>
                  setExpandedToolId(current => (current === message.tool.id ? null : message.tool.id))
                }
              />
            ) : (
              <Bubble role={message.role} sx={message.role === 'error' ? { color: 'error.main' } : undefined}>
                <Box sx={{ whiteSpace: 'pre-wrap' }}>{message.text}</Box>
              </Bubble>
            )}
            {message.role === 'assistant' && (
              <Box sx={{ display: 'flex', gap: '10px', m: '-6px 0 12px 4px' }}>
                <IconButton
                  size="small"
                  aria-label="Good answer"
                  aria-pressed={replyFeedback.ratingOf(index) === 'up'}
                  color={replyFeedback.ratingOf(index) === 'up' ? 'primary' : 'default'}
                  onClick={() => replyFeedback.rate(index, 'up')}
                  sx={{ p: '2px' }}
                >
                  <ThumbUpOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label="Bad answer"
                  aria-pressed={replyFeedback.ratingOf(index) === 'down'}
                  color={replyFeedback.ratingOf(index) === 'down' ? 'primary' : 'default'}
                  onClick={() => replyFeedback.rate(index, 'down')}
                  sx={{ p: '2px' }}
                >
                  <ThumbDownOutlinedIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            )}
            {message.role === 'assistant' && replyFeedback.showsFollowUp(index) && (
              <Bubble role="assistant" sx={{ maxWidth: '100%', width: '100%' }}>
                <ReplyFeedbackForm index={index} feedback={replyFeedback} />
              </Bubble>
            )}
          </Fragment>
        ))}
        {status && (
          <Bubble
            role="assistant"
            sx={{
              fontStyle: 'italic',
              color: 'text.secondary',
              animation: 'copilotStatusPulse 1.4s ease-in-out infinite',
              '@keyframes copilotStatusPulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.55 } },
            }}
          >
            {status}
          </Bubble>
        )}
      </Box>
      <Box
        component="form"
        onSubmit={event => {
          event.preventDefault();
          send(draft);
        }}
        sx={{ px: 2, pt: 1.5, pb: 2 }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-end',
            gap: 1,
            border: 1,
            borderColor: 'divider',
            borderRadius: '14px',
            bgcolor: 'background.paper',
            px: 1.5,
            py: '10px',
            '&:focus-within': { borderColor: 'primary.main' },
          }}
        >
          <InputBase
            multiline
            maxRows={6}
            fullWidth
            placeholder="Ask about your endpoints"
            value={draft}
            onChange={event => setDraft(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault();
                send(draft);
              }
            }}
            sx={{ fontSize: 13.5, lineHeight: 1.4, p: 0 }}
          />
          <IconButton
            type="submit"
            aria-label="Send"
            disabled={!draft.trim() || running}
            sx={{
              flex: '0 0 auto',
              width: 30,
              height: 30,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              '&:hover': { bgcolor: 'primary.dark' },
              '&.Mui-disabled': { bgcolor: 'primary.main', color: 'primary.contrastText', opacity: 0.35 },
            }}
          >
            <ArrowUpwardIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>
    </Paper>
  );

  return (
    <Fragment>
      {!open && (
        <Fab color="primary" size="medium" aria-label="Open Copilot" sx={LAUNCHER_SX} onClick={() => setOpen(true)}>
          <ChatBubbleOutlineIcon />
        </Fab>
      )}
      {open && window_}
      <Dialog open={Boolean(approval)} onClose={() => resolveApproval(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm action</DialogTitle>
        <DialogContent>
          <Typography variant="body1">{approval ? approval.summary : ''}</Typography>
          {approval && (
            <Box component="details" sx={{ mt: 2 }}>
              <Box component="summary" sx={{ cursor: 'pointer' }}>
                Details
              </Box>
              {approval.risk && (
                <Typography variant="body2" color="warning.main" sx={{ mt: 1.5 }}>
                  {approval.risk}
                </Typography>
              )}
              <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
                {`${approval.tool}\n${JSON.stringify(approval.input, null, 2)}`}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => resolveApproval(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => resolveApproval(true)}>
            Approve
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
}
